import React, { useState, useEffect } from 'react';
import { FaHeart, FaMapMarkerAlt, FaBriefcase, FaUserCheck, FaTrash, FaStar, FaGraduationCap, FaVenusMars, FaPrayingHands } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../services/api';

const Shortlisted = ({ onViewProfile }) => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);
    const [myProfile, setMyProfile] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [shortlistedResponse, myProfileResponse] = await Promise.all([
                    api.get('/shortlisted'),
                    api.get('/dashboard/my-profile')
                ]);
                setProfiles(shortlistedResponse.data || []);
                setMyProfile(myProfileResponse.data.profile);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Helper to get correct image URL
    const getProfileImage = (photoPath, gender) => {
        if (!photoPath) {
            return gender === 'Female'
                ? 'https://randomuser.me/api/portraits/women/85.jpg'
                : 'https://randomuser.me/api/portraits/men/85.jpg';
        }

        if (photoPath.startsWith('http') || photoPath.startsWith('data:') || photoPath.startsWith('blob:')) {
            return photoPath;
        }

        const BASE_URL = api.defaults.baseURL;
        const baseUrl = BASE_URL.replace("/api", "");

        const cleanPath = photoPath.startsWith('/') ? photoPath : `/${photoPath}`;
        return `${baseUrl}${cleanPath}`;
    };

    const calculateMatchPercentage = (profile) => {
        if (!myProfile) return 85;

        let score = 50;

        // Religion match (20 points)
        if (myProfile.religionCommunity?.religion === profile.religionCommunity?.religion) score += 20;

        // Caste match (15 points)
        if (myProfile.religionCommunity?.caste === profile.religionCommunity?.caste) score += 15;

        // Education match (10 points)
        if (myProfile.educationCareer?.education === profile.educationCareer?.education) score += 10;

        // Location match (10 points)
        if (myProfile.location?.currentCity === profile.location?.currentCity) score += 10;

        // Age compatibility (10 points)
        if (myProfile.basicInfo?.dob && profile.basicInfo?.dob) {
            const calculateExactAge = (dobString) => {
                if (!dobString) return 0;
                const dob = new Date(dobString);
                const today = new Date();
                let ageVal = today.getFullYear() - dob.getFullYear();
                const m = today.getMonth() - dob.getMonth();
                if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                    ageVal--;
                }
                return ageVal;
            };
            const myAge = calculateExactAge(myProfile.basicInfo.dob);
            const theirAge = calculateExactAge(profile.basicInfo.dob);
            if (Math.abs(myAge - theirAge) <= 5) score += 10;
        }

        // Occupation match (5 points)
        if (myProfile.educationCareer?.occupation === profile.educationCareer?.occupation) score += 5;

        return Math.min(score, 99);
    };

    const handleRemoveShortlist = async (profileId, e) => {
        e.stopPropagation();
        setRemovingId(profileId);

        try {
            await api.post('/shortlist', { profileId });
            setTimeout(() => {
                setProfiles(profiles.filter(p => p._id !== profileId));
                setRemovingId(null);
            }, 400);
        } catch (error) {
            console.error('Error removing from shortlist:', error);
            setRemovingId(null);
        }
    };

    const handleViewProfile = (profile) => {
        if (onViewProfile) {
            onViewProfile(profile);
        }
    };

    if (loading) {
        return (
            <div className="p-6 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-600 mx-auto"></div>
                <p className="mt-4 text-text-secondary">Loading shortlisted profiles...</p>
            </div>
        );
    }

    return (
        <div className="w-full mx-auto px-4 py-8">
            {/* Header */}
            <div className="mb-8">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-text-primary drop-shadow-sm mb-2">My Shortlisted Profiles</h1>
                        <p className="text-text-secondary">
                            Profiles you've shown interest in.
                            <span className="font-semibold text-primary-pink ml-1">{profiles.length} profiles</span>
                        </p>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-center">
                            <div className="text-2xl font-bold text-primary-pink">{profiles.length}</div>
                            <div className="text-sm text-text-muted">Shortlisted</div>
                        </div>
                        <div className="h-8 w-px bg-[rgba(255,255,255,0.08)]"></div>
                        <div className="text-center">
                            
                        </div>
                    </div>
                </div>
            </div>

            {profiles.length === 0 ? (
                <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-lg border border-glass-border p-12 text-center">
                    <div className="text-5xl mb-4 text-text-muted">💝</div>
                    <h3 className="text-xl font-semibold text-text-primary mb-2">No shortlisted profiles yet</h3>
                    <p className="text-text-muted">
                        Start shortlisting profiles you're interested in from the Matches page!
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <AnimatePresence>
                        {profiles.map((profile) => {
                            const photoUrl = profile.photos?.[0];
                            const gender = profile.basicInfo?.gender;
                            const isRemoving = removingId === profile._id;
                            const matchPercentage = calculateMatchPercentage(profile);
                            const calculateExactAge = (dobString) => {
                                if (!dobString) return 'N/A';
                                const dob = new Date(dobString);
                                const today = new Date();
                                let ageVal = today.getFullYear() - dob.getFullYear();
                                const m = today.getMonth() - dob.getMonth();
                                if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) {
                                    ageVal--;
                                }
                                return ageVal;
                            };
                            
                            const age = calculateExactAge(profile.basicInfo?.dob);

                            return (
                                <motion.div
                                    key={profile._id}
                                    layout
                                    initial={{ opacity: 1, scale: 1 }}
                                    exit={{
                                        opacity: 0,
                                        scale: 0.9,
                                        y: -50,
                                        transition: { duration: 0.4 }
                                    }}
                                    onClick={() => handleViewProfile(profile)}
                                    className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass rounded-lg border border-glass-border hover:border-primary-pink/50 shadow-[0_0_15px_rgba(255,46,136,0.2)] hover:shadow-lg transition-all duration-300 overflow-hidden cursor-pointer group relative"
                                >
                                    {/* Remove Animation Overlay */}
                                    <AnimatePresence>
                                        {isRemoving && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="absolute inset-0 bg-red-500/90 z-20 flex flex-col items-center justify-center text-white"
                                            >
                                                <FaTrash className="text-4xl mb-3 animate-bounce" />
                                                <p className="text-lg font-bold">Removed!</p>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>

                                    {/* Profile Image */}
                                    <div className="relative h-64 bg-[rgba(255,255,255,0.05)] overflow-hidden">
                                        <img
                                            src={getProfileImage(photoUrl, gender)}
                                            alt={profile.basicInfo?.firstName || 'Profile'}
                                            onError={(e) => {
                                                e.currentTarget.onerror = null;
                                                e.currentTarget.src = gender === 'Female'
                                                    ? 'https://randomuser.me/api/portraits/women/85.jpg'
                                                    : 'https://randomuser.me/api/portraits/men/85.jpg';
                                            }}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />

                                        {/* Overlay */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                        {/* Match Percentage Badge */}
                                        {/* <div className="absolute top-3 left-3">
        <div className="bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary/90 backdrop-blur-sm px-3 py-1 rounded-full flex items-center gap-1 shadow-sm">
            <FaStar className="text-yellow-500" size={12} />
            <span className="text-sm font-bold">{matchPercentage}%</span>
        </div>
    </div> */}

                                        {/* Remove Button */}
                                        <button
                                            onClick={(e) => handleRemoveShortlist(profile._id, e)}
                                            className="absolute top-3 right-3 p-2 bg-glass-card backdrop-blur-xl border border-glass-border shadow-glass text-text-primary/90 backdrop-blur-sm rounded-full text-primary-pink hover:bg-red-50 hover:text-red-600 transition-all shadow-sm"
                                            title="Remove from shortlist"
                                        >
                                            <FaHeart className="text-lg" />
                                        </button>

                                        {/* Verified Badge */}
                                        {profile.isVerified && (
                                            <div className="absolute bottom-3 left-3">
                                                <div className="bg-[rgba(59,130,246,0.1)]0 text-white p-1.5 rounded-full">
                                                    <FaUserCheck size={12} />
                                                </div>
                                            </div>
                                        )}

                                        {/* Profile Info Overlay */}
                                        <div className="absolute bottom-0 left-0 right-0 p-4">
                                            <h3 className="text-lg font-bold text-white mb-1">
                                                {profile.basicInfo?.firstName}, {age}
                                            </h3>
                                            <div className="flex items-center text-white/90 text-sm">
                                                <FaMapMarkerAlt size={12} className="mr-1" />
                                                <span>{profile.location?.currentCity || 'N/A'}</span>
                                            </div>
                                        </div>

                                        {/* Subtle diagonal center watermark - much more professional */}
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                            <span className="text-white text-2xl font-semibold tracking-widest opacity-30 rotate-[-30deg] select-none">
                                                Kalyana Matrimony
                                            </span>
                                        </div>
                                    </div>

                                    {/* Profile Details */}
                                    <div className="p-4">
                                        <div className="grid grid-cols-2 gap-3 mb-4">
                                            <div className="flex items-center gap-2">
                                                <FaBriefcase className="text-text-muted" size={14} />
                                                <span className="text-sm text-text-secondary truncate">
                                                    {profile.educationCareer?.occupation || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FaGraduationCap className="text-text-muted" size={14} />
                                                <span className="text-sm text-text-secondary truncate">
                                                    {profile.educationCareer?.education || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FaPrayingHands className="text-text-muted" size={14} />
                                                <span className="text-sm text-text-secondary truncate">
                                                    {profile.religionCommunity?.religion || 'N/A'}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <FaVenusMars className="text-text-muted" size={14} />
                                                <span className="text-sm text-text-secondary truncate">
                                                    {profile.basicInfo?.gender || 'N/A'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Match Score Bar */}
                                        <div className="mb-4">
                                            <div className="flex justify-between items-center mb-1">
                                                <span className="text-xs text-text-muted">Match Score</span>
                                                <span className="text-xs font-bold text-primary-pink">{matchPercentage}%</span>
                                            </div>
                                            <div className="w-full bg-[rgba(255,255,255,0.08)] rounded-full h-2">
                                                <div
                                                    className="bg-gradient-to-r from-rose-500 to-pink-500 h-2 rounded-full transition-all duration-500"
                                                    style={{ width: `${matchPercentage}%` }}
                                                ></div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewProfile(profile);
                                            }}
                                            className="w-full bg-primary-pink text-white py-2.5 rounded-lg font-medium hover:bg-opacity-80 hover:shadow-[0_0_20px_rgba(255,46,136,0.6)] transition-colors text-sm"
                                        >
                                            View Full Profile
                                        </button>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </AnimatePresence>
                </div>
            )}
        </div>
    );
};

export default Shortlisted;